import { Station, ProjectConfig } from '../../types';
import { MatrixType } from '../url/matrixMapping';
import { MATRIX_DEFINITIONS } from '../url/matrixMapping';
import { formatCurrencyNoDecimals } from '../../lib/format/currency';
import { getSortedStationIndices, getSortedStations, reorderMatrix } from '../matrix/ordering';
import { getEffectivePopulation } from '../../utils/population';

export function isStationsMatrix(matrix: number[][] | Station[]): matrix is Station[] {
  return matrix.length > 0 && typeof matrix[0] === 'object' && 'id' in matrix[0];
}

export function generateStationsCSV(stations: Station[]): string {
  const headers = ['Station', 'Population within 30km', 'Arrivals', 'Revenue', 'Share of Total Revenue', 'Centrality'];
  const sortedStations = getSortedStations(stations);
  const rows = sortedStations.map(station => [
    `"${station.name}"`,
    getEffectivePopulation(station).toString(),
    station.annualArrivals?.toString() || '0',
    station.annualRevenue?.toString() || '0',
    station.revenuePercentage ? (station.revenuePercentage / 100).toFixed(3) : '0.000',
    station.centrality?.toString() || '0'
  ]);

  return [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
}

export function generateMatrixContent(
  type: MatrixType,
  data: number[][] | Station[],
  stations: Station[],
  config: ProjectConfig
): string {
  if (type === 'cities') {
    const sortedStations = getSortedStations(data as Station[]);
    return generateStationsCSV(sortedStations);
  }

  const numericMatrix = data as number[][];
  const sortedIndices = getSortedStationIndices(stations);
  const sortedStations = getSortedStations(stations);
  const reorderedMatrix = reorderMatrix(numericMatrix, sortedIndices);

  const headers = ['City', ...sortedStations.map(s => `"${s.name}"`)];
  const rows = sortedStations.map((station, i) => [
    `"${station.name}"`,
    ...reorderedMatrix[i].map(value => {
      if (type === 'constructionCostPerTicket') {
        return value === 0 ? '0' : value.toFixed(3);
      } else if (type === 'gravity') {
        return value === 0 ? '0' : value.toFixed(4);
      } else if (type === 'trainEmissions') {
        return value === 0 ? '0' : value.toFixed(3);
      } else if (type === 'flyingEmissions') {
        return value === 0 ? '0' : value.toFixed(3);
      } else if (type === 'drivingEmissions') {
        return value === 0 ? '0' : value.toFixed(3);
      } else if (type === 'travelTime' || type === 'flightTime') {
        // Convert to fraction of a day with minute precision (1/1440 = 1 minute)
        return (Math.round(value * 1440) / 1440).toFixed(4);
      } else if (MATRIX_DEFINITIONS[type].hasCurrency) {
        return Math.round(value).toString();
      } else {
        // For distances and other numeric matrices, don't use thousands separators
        return Math.round(value).toString();
      }
    })
  ]);

  return [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
} 